'use client';

/**
 * Architecture Capability Widget (Pixi.js v8)
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import { api } from '@/lib/api/client';
import { Loader2, Network, Target } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { clientLog } from '@/lib/client-logger';
import {
  capabilityLayout,
  COLORS,
  ArchNode,
  ArchEdge,
  PositionedGraph,
} from '@/lib/layout/capability-layout';

const COMPONENT = 'ArchCapabilityPixi';

// Theme colors
const THEME = {
  dark: {
    background: 0x0a0a0a,
    nodeBackground: 0x1e1e2e,
    featureBackground: 0x1a1a2e,
    textPrimary: '#ffffff',
    textSecondary: '#cccccc',
    textMuted: '#888888',
  },
  light: {
    background: 0xf5f5f5,
    nodeBackground: 0xffffff,
    featureBackground: 0xfafafa,
    textPrimary: '#1a1a1a',
    textSecondary: '#444444',
    textMuted: '#666666',
  },
};

type ThemeColors = typeof THEME.dark;

// =============================================================================
// NODE RENDERERS (OOP Pattern)
// =============================================================================

interface RenderResult {
  graphics: any;
  texts: any[];
}

interface NodeRenderer {
  render(node: any, PIXI: any, colors: ThemeColors): RenderResult;
}

class SystemNodeRenderer implements NodeRenderer {
  render(node: any, PIXI: any, colors: ThemeColors): RenderResult {
    const g = new PIXI.Graphics();
    g.circle(node.x, node.y, node.radius);
    g.fill({ color: node.color, alpha: 0.3 });
    g.stroke({ width: 3, color: node.color });

    const txt = new PIXI.Text({
      text: node.label,
      style: { fontSize: 18, fill: colors.textPrimary, fontFamily: 'Arial', fontWeight: 'bold' }
    });
    txt.anchor.set(0.5);
    txt.x = node.x;
    txt.y = node.y + node.radius + 20;

    return { graphics: g, texts: [txt] };
  }
}

class CapabilityNodeRenderer implements NodeRenderer {
  private static readonly WIDTH = 200;
  private static readonly HEIGHT = 90;

  render(node: any, PIXI: any, colors: ThemeColors): RenderResult {
    const { WIDTH, HEIGHT } = CapabilityNodeRenderer;
    const g = new PIXI.Graphics();
    g.roundRect(node.x - WIDTH/2, node.y - HEIGHT/2, WIDTH, HEIGHT, 10);
    g.fill({ color: colors.nodeBackground });
    g.stroke({ width: 2, color: node.color, alpha: 0.8 });

    const texts: any[] = [];

    const label = new PIXI.Text({
      text: node.label,
      style: { fontSize: 15, fill: colors.textPrimary, fontFamily: 'Arial', wordWrap: true, wordWrapWidth: 180, align: 'center' }
    });
    label.anchor.set(0.5);
    label.x = node.x;
    label.y = node.y + 6;
    texts.push(label);

    if (node.clusterLabel) {
      const cluster = new PIXI.Text({
        text: node.clusterLabel.toUpperCase(),
        style: { fontSize: 11, fill: '#BD10E0', fontFamily: 'Arial', fontWeight: 'bold' }
      });
      cluster.anchor.set(0.5);
      cluster.x = node.x;
      cluster.y = node.y - HEIGHT/2 + 14;
      texts.push(cluster);
    }

    return { graphics: g, texts };
  }
}

class FeatureNodeRenderer implements NodeRenderer {
  private static readonly WIDTH = 180;
  private static readonly HEIGHT = 54;

  render(node: any, PIXI: any, colors: ThemeColors): RenderResult {
    const { WIDTH, HEIGHT } = FeatureNodeRenderer;
    const g = new PIXI.Graphics();
    g.roundRect(node.x - WIDTH/2, node.y - HEIGHT/2, WIDTH, HEIGHT, 6);
    g.fill({ color: colors.featureBackground });
    g.stroke({ width: 1.5, color: node.color, alpha: 0.7 });

    const txt = new PIXI.Text({
      text: node.label,
      style: { fontSize: 14, fill: colors.textSecondary, fontFamily: 'Arial', wordWrap: true, wordWrapWidth: 160 }
    });
    txt.anchor.set(0.5);
    txt.x = node.x;
    txt.y = node.y;

    return { graphics: g, texts: [txt] };
  }
}

const NODE_RENDERERS: Record<string, NodeRenderer> = {
  system: new SystemNodeRenderer(),
  capability: new CapabilityNodeRenderer(),
  feature: new FeatureNodeRenderer(),
};

interface ArchData {
  nodes: ArchNode[];
  edges: ArchEdge[];
  metadata: { packages: number; capabilities: number; features: number };
}

export function ArchCapabilityPixi() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerMountedForResize, setContainerMountedForResize] = useState(false);
  const containerCallback = (node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node) setContainerMountedForResize(true);
  };
  const pixiContainerRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<ArchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCaps, setExpandedCaps] = useState<Set<string>>(new Set());
  const [pixiReady, setPixiReady] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [containerMounted, setContainerMounted] = useState(false);

  const appRef = useRef<any>(null);
  const pixiModuleRef = useRef<any>(null);

  const { resolvedTheme } = useTheme();
  const colors = resolvedTheme === 'light' ? THEME.light : THEME.dark;

  // Callback ref to detect when container is mounted
  const pixiContainerCallback = (node: HTMLDivElement | null) => {
    pixiContainerRef.current = node;
    if (node) {
      setContainerMounted(true);
    }
  };

  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      const startTime = performance.now();
      try {
        const result = await api.get<ArchData>('/docs/arch/canvas');
        setData(result);

        clientLog.info(COMPONENT, 'Data fetched', {
          nodes: result.nodes?.length || 0,
          edges: result.edges?.length || 0,
          durationMs: Math.round(performance.now() - startTime),
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load';
        setError(errorMessage);
        clientLog.error(COMPONENT, 'Data fetch failed', { error: errorMessage });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Track dimensions from container via ResizeObserver
  useEffect(() => {
    if (!containerMountedForResize || !containerRef.current) {
      clientLog.debug(COMPONENT, 'ResizeObserver waiting', { containerMountedForResize });
      return;
    }

    clientLog.debug(COMPONENT, 'ResizeObserver attached');

    const update = () => {
      const { clientWidth, clientHeight } = containerRef.current!;
      clientLog.debug(COMPONENT, 'Container measured', { clientWidth, clientHeight });
      if (clientWidth > 0 && clientHeight > 0) {
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    };
    update();
    const obs = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      clientLog.debug(COMPONENT, 'ResizeObserver fired', { width: rect?.width, height: rect?.height });
      update();
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [containerMountedForResize]);

  // Compute layout
  const graph = useMemo(() => {
    if (!data || dimensions.width === 0) return null;
    return capabilityLayout(data.nodes, data.edges, {
      width: dimensions.width,
      height: dimensions.height,
      expandedCapabilities: expandedCaps,
    });
  }, [data, dimensions, expandedCaps]);

  // Initialize Pixi when container is mounted
  useEffect(() => {
    // Skip if not ready
    if (!containerMounted || appRef.current || !pixiContainerRef.current) return;

    let destroyed = false;
    let canvas: HTMLCanvasElement | null = null;

    const handleContextLost = (e: Event) => {
      e.preventDefault();
      console.warn('[ArchCapabilityPixi] WebGL context lost - will attempt recovery');
      setPixiReady(false);
    };

    const handleContextRestored = () => {
      console.log('[ArchCapabilityPixi] WebGL context restored - reinitializing');
      // Destroy and reinit
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
      init();
    };

    const init = async () => {
      const startTime = performance.now();
      try {
        clientLog.debug(COMPONENT, 'Initializing Pixi.js', { dimensions });

        const PIXI = await import('pixi.js');
        if (destroyed) return;

        pixiModuleRef.current = PIXI;

        const app = new PIXI.Application();

        await app.init({
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: colors.background,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (destroyed) {
          app.destroy(true);
          return;
        }

        // Append canvas to container
        pixiContainerRef.current?.appendChild(app.canvas);
        appRef.current = app;
        canvas = app.canvas as HTMLCanvasElement;

        // Add context loss handlers
        canvas.addEventListener('webglcontextlost', handleContextLost);
        canvas.addEventListener('webglcontextrestored', handleContextRestored);

        setPixiReady(true);

        clientLog.info(COMPONENT, 'Pixi initialized', {
          width: dimensions.width,
          height: dimensions.height,
          resolution: window.devicePixelRatio || 1,
          durationMs: Math.round(performance.now() - startTime),
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('Pixi init error:', err);
        setError('Failed to initialize renderer');
        clientLog.error(COMPONENT, 'Pixi init failed', { error: errorMessage });
      }
    };

    init();

    return () => {
      destroyed = true;
      if (canvas) {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      }
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
        clientLog.debug(COMPONENT, 'Pixi destroyed');
      }
    };
  }, [containerMounted]); // Run when container mounts

  // Resize - update renderer and hit area
  useEffect(() => {
    if (!pixiReady || !appRef.current || !pixiModuleRef.current) return;
    const PIXI = pixiModuleRef.current;
    const { width, height } = dimensions;

    // Resize the WebGL renderer
    appRef.current.renderer.resize(width, height);

    // Update hit area so interactions work at edges
    appRef.current.stage.hitArea = new PIXI.Rectangle(0, 0, width, height);

    clientLog.debug(COMPONENT, 'Canvas resized', { width, height });
  }, [dimensions, pixiReady]);

  // Update background on theme change
  useEffect(() => {
    if (!pixiReady || !appRef.current) return;
    appRef.current.renderer.background.color = colors.background;
  }, [colors.background, pixiReady]);

  // Render graph
  useEffect(() => {
    if (!pixiReady || !graph || !appRef.current || !pixiModuleRef.current) return;

    const startTime = performance.now();
    const PIXI = pixiModuleRef.current;
    const app = appRef.current;

    // Clear
    app.stage.removeChildren();

    // Main container with transform
    const main = new PIXI.Container();
    app.stage.addChild(main);

    // Fit view
    const sw = app.screen.width;
    const sh = app.screen.height;
    const scaleX = (sw * 0.9) / graph.bounds.width;
    const scaleY = (sh * 0.9) / graph.bounds.height;
    const scale = Math.min(scaleX, scaleY, 1.2);

    main.scale.set(scale);
    main.x = sw / 2 - graph.bounds.centerX * scale;
    main.y = sh / 2 - graph.bounds.centerY * scale;

    // Draw edges first
    const edges = new PIXI.Graphics();
    graph.edges.forEach((e) => {
      edges.moveTo(e.sourceX, e.sourceY);
      edges.lineTo(e.targetX, e.targetY);
      edges.stroke({ width: e.width, color: e.color, alpha: e.alpha });
    });
    main.addChild(edges);

    // Collect text elements to add after graphics (z-order fix)
    const textElements: any[] = [];

    // Draw nodes using OOP renderers
    graph.nodes.forEach((node) => {
      const renderer = NODE_RENDERERS[node.type];
      if (!renderer) return;

      const { graphics: g, texts } = renderer.render(node, PIXI, colors);
      texts.forEach((t) => textElements.push(t));

      // Make interactive
      g.eventMode = 'static';
      g.cursor = 'pointer';
      g.on('pointertap', () => {
        const archNode = data?.nodes.find((n) => n.id === node.id);
        if (archNode) {
          selectNode({
            id: archNode.id,
            label: archNode.label,
            type: archNode.type,
            properties: {
              description: archNode.description,
              ...(archNode.extra || {}),
            },
          });
          clientLog.info(COMPONENT, 'Node selected', {
            nodeId: archNode.id,
            nodeType: archNode.type,
            nodeLabel: archNode.label,
          });
          if (archNode.type === 'capability') {
            setExpandedCaps((prev) => {
              const next = new Set(prev);
              const wasExpanded = next.has(node.id);
              wasExpanded ? next.delete(node.id) : next.add(node.id);
              clientLog.info(COMPONENT, 'Capability toggled', {
                nodeId: node.id,
                expanded: !wasExpanded,
              });
              return next;
            });
          }
        }
      });

      main.addChild(g);
    });

    // Add text elements on top of all graphics
    textElements.forEach((txt) => main.addChild(txt));

    clientLog.debug(COMPONENT, 'Graph rendered', {
      nodeCount: graph.nodes.length,
      edgeCount: graph.edges.length,
      expandedCaps: expandedCaps.size,
      durationMs: Math.round(performance.now() - startTime),
    });

  }, [pixiReady, graph, data, selectNode, expandedCaps, colors]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <Network className="w-8 h-8 mb-2" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  const capCount = data?.nodes.filter((n) => n.type === 'capability').length || 0;

  if (capCount === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Target className="w-8 h-8 opacity-50" />
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <div ref={containerCallback} className="absolute inset-0">
        <div ref={pixiContainerCallback} className="absolute inset-0" />
      </div>

      {!pixiReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      <div className="absolute bottom-2 left-2 z-10 bg-black/80 rounded px-2 py-1 text-[9px] text-muted-foreground">
        Pixi.js | {capCount}c | {expandedCaps.size} exp | Click to expand
      </div>
    </div>
  );
}
